import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { Badge, EmptyState, TableSkeleton } from "../../../components/ui";
import { getVideoPerformanceDetail } from "../../../services/videoPerformance";

const muted = { colors: "#9b9ba6" };
const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const dayLabel = (key) => new Date(`${key}T00:00:00Z`).toLocaleDateString([], { day: "numeric", month: "short", timeZone: "UTC" });
const base = (id, days) => {
  // Une date sur `step` sur l'axe (8 au plus), la plus récente toujours affichée
  const step = Math.max(1, Math.ceil(days.length / 8));
  const shown = new Set(days.filter((d, i) => (days.length - 1 - i) % step === 0));
  return baseOptions(id, days, shown);
};
const baseOptions = (id, days, shown) => ({
  chart: { id, background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: "inherit" },
  dataLabels: { enabled: false },
  grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
  legend: { position: "top", horizontalAlign: "right", labels: muted },
  labels: days,
  xaxis: { type: "category", labels: { style: muted, rotate: 0, hideOverlappingLabels: false, formatter: (v) => (v && shown.has(v) ? dayLabel(v) : "") }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
  tooltip: { theme: "dark", shared: true, intersect: false, x: { formatter: (v, { dataPointIndex }) => (days[dataPointIndex] ? dayLabel(days[dataPointIndex]) : "") } },
});

/** Views per day (columns) and plays started (line). */
export const audienceChart = (daily, t, id = "perf-audience") => {
  const days = daily.map((d) => d.day);
  const options = base(id, days);
  return {
    series: [
      { name: t("Views"), type: "column", data: daily.map((d) => d.views) },
      { name: t("Plays started"), type: "line", data: daily.map((d) => d.started) },
    ],
    options: {
      ...options,
      colors: ["#1176BC", "#CDAE6E"],
      stroke: { width: [0, 3], curve: "smooth" },
      markers: { size: daily.length <= 14 ? 3 : 0, strokeWidth: 0 },
      plotOptions: { bar: { columnWidth: daily.length > 40 ? "80%" : "55%", borderRadius: 2 } },
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => fmt(Math.round(v)) } },
    },
  };
};

/** Comments and likes per day (and shares, for a short). */
export const engagementChart = (daily, t, withShares = false) => {
  const days = daily.map((d) => d.day);
  const options = base("perf-engagement", days);
  return {
    series: [
      { name: t("Comments"), data: daily.map((d) => d.comments) },
      { name: t("Likes"), data: daily.map((d) => d.likes) },
      ...(withShares ? [{ name: t("Shares"), data: daily.map((d) => d.shares || 0) }] : []),
    ],
    options: {
      ...options,
      colors: ["#8b7cf6", "#ef5d8f", "#27aae1"],
      plotOptions: { bar: { columnWidth: daily.length > 40 ? "80%" : "55%", borderRadius: 2 } },
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => fmt(Math.round(v)) } },
    },
  };
};

/** Where the viewers stop watching (share of plays per bucket). */
export const dropOffChart = (dropOff, t) => ({
  series: [{ name: t("Plays"), data: dropOff.buckets.map((b) => b.share) }],
  options: {
    chart: { id: "perf-dropoff", background: "transparent", toolbar: { show: false }, animations: { enabled: false }, fontFamily: "inherit" },
    plotOptions: { bar: { horizontal: true, barHeight: "60%", borderRadius: 3, distributed: true } },
    colors: ["#ef5d8f", "#f5a524", "#CDAE6E", "#6FB3E0", "#1176BC", "#22c58b"],
    dataLabels: { enabled: true, formatter: (v) => `${v} %`, style: { colors: ["#fff"] } },
    legend: { show: false },
    grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
    xaxis: { categories: dropOff.buckets.map((b) => t(b.label)), max: 100, labels: { style: muted, formatter: (v) => `${v} %` } },
    yaxis: { labels: { style: muted } },
    tooltip: { theme: "dark", y: { formatter: (v, { dataPointIndex }) => `${v} % (${fmt(dropOff.buckets[dataPointIndex].count)})` } },
  },
});

/** Watch time: « 45 s », « 12 min », « 2 h 05 ». */
const duration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.floor(s / 3600)} h ${String(Math.round((s % 3600) / 60)).padStart(2, "0")}`;
};

/** Gap with the average of the videos of the list, in points. */
const Versus = ({ value, average }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined || average === null || average === undefined) return null;
  const gap = value - average;
  const tone = gap > 0 ? "up" : gap < 0 ? "down" : "flat";
  return (
    <small className="perf-delta" data-tone={tone}>
      {gap > 0 ? "+" : ""}
      {gap} pts {t("vs average ({{value}} %)", { value: average })}
    </small>
  );
};

/**
 * One video over the period: audience per day, how far people watch compared with the other
 * videos, engagement per day, drop-off. Previous / next go through the rows of the table.
 * `period` is `{ period }` or `{ period: "custom", start, end }`.
 */
const VideoPerformanceDetail = ({ id, period, averages, onClose, onPrevious, onNext }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const periodKey = JSON.stringify(period);
  useEffect(() => {
    setData(null);
    setError(false);
    getVideoPerformanceDetail(id, JSON.parse(periodKey))
      .then(setData)
      .catch(() => setError(true));
  }, [id, periodKey]);
  // Flèches du clavier : vidéo précédente ou suivante du tableau
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;
      if (e.key === "ArrowLeft" && onPrevious) onPrevious();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPrevious, onNext]);

  const audience = data ? audienceChart(data.daily, t) : null;
  const engagement = data ? engagementChart(data.daily, t, data.isShort || data.shares > 0) : null;
  const drop = data?.dropOff.plays ? dropOffChart(data.dropOff, t) : null;
  const sum = (key) => (data ? data.daily.reduce((n, d) => n + (d[key] || 0), 0) : 0);
  const plays = data?.plays;
  const rated = plays && plays.started >= 5;

  return (
    <Modal show={true} onHide={onClose} centered size="xl" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>{data?.title || t("Video performance")}</Modal.Title>
        <div className="perf-detail-nav">
          <button type="button" className="ui-btn ui-btn-secondary" onClick={onPrevious || undefined} disabled={!onPrevious} aria-label={t("Previous video")} title={t("Previous video")}>
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={onNext || undefined} disabled={!onNext} aria-label={t("Next video")} title={t("Next video")}>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className="perf-detail">
        {error ? (
          <EmptyState icon="ri-error-warning-line">{t("The list could not be loaded.")}</EmptyState>
        ) : !data ? (
          <TableSkeleton rows={6} />
        ) : (
          <>
            <div className="perf-detail-head">
              {data.thumbnail && <img src={data.thumbnail} alt="" />}
              <div>
                <span className="ui-hint">
                  {t("Published {{date}}", { date: new Date(data.publishedAt).toLocaleDateString() })}
                  {data.duration ? ` · ${duration(data.duration)}` : ""}
                  {!data.online && " · "}
                  {!data.online && <Badge color="slate">{t("Not online")}</Badge>}
                </span>
                <div className="perf-detail-figures">
                  <span><strong>{fmt(sum("views"))}</strong> {t("views over the period")}</span>
                  <span><strong>{fmt(data.totalViews)}</strong> {t("in total")}</span>
                  <span><strong>{fmt(sum("started"))}</strong> {t("plays started")}</span>
                  {plays?.watchSeconds > 0 && <span><strong>{duration(plays.watchSeconds)}</strong> {t("watched")}</span>}
                  {data.shares > 0 && <span><strong>{fmt(data.shares)}</strong> {t("shares")}</span>}
                  {data.jwCompleteRate !== null && <span><strong>{data.jwCompleteRate} %</strong> {t("completion (JW)")}</span>}
                </div>
                <Link to={`/dashboard/video-info/${data.id}`} className="ui-hint" onClick={onClose}>
                  {t("Open the video page")} <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
            {rated && (
              <div className="ledger-stats perf-detail-rates">
                <div className="ui-card ledger-stat">
                  <strong>{plays.completion} %</strong>
                  <span>{t("Plays watched to the end")}</span>
                  <Versus value={plays.completion} average={averages?.avgCompletion} />
                </div>
                <div className="ui-card ledger-stat">
                  <strong>{plays.watched} %</strong>
                  <span>{t("Average share watched")}</span>
                  <Versus value={plays.watched} average={averages?.avgWatched} />
                </div>
                <div className="ui-card ledger-stat">
                  <strong>{duration(plays.watchSeconds / plays.started)}</strong>
                  <span>{t("Watched per play")}</span>
                </div>
              </div>
            )}
            <section>
              <h3>{t("Audience per day")}</h3>
              <Chart type="line" height={240} options={audience.options} series={audience.series} />
            </section>
            <div className="perf-detail-columns">
              <section>
                <h3>{t("Where viewers stop")}</h3>
                {drop ? (
                  <>
                    <Chart type="bar" height={240} options={drop.options} series={drop.series} />
                    <p className="ui-hint">{t("{{count}} plays in the app and on the site over the period.", { count: data.dropOff.plays })}</p>
                  </>
                ) : (
                  <p className="ui-hint">{t("No play recorded over the period.")}</p>
                )}
              </section>
              <section>
                <h3>{t("Comments and likes per day")}</h3>
                <Chart type="bar" height={240} options={engagement.options} series={engagement.series} />
              </section>
            </div>
            {data.period === "all" && <p className="ui-hint">{t("« All time »: the charts cover the last 90 days.")}</p>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Close")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default VideoPerformanceDetail;
